/**
 * Helper functions to replace lodash for better tree-shaking and reduced bundle size
 */

/**
 * Custom debounce function to replace lodash debounce
 * Returns a debounced function with cancel method for compatibility
 */
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) & { cancel: () => void } {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  const debouncedFn = function (...args: Parameters<T>) {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
  
  // Add cancel method for compatibility with lodash debounce
  debouncedFn.cancel = function () {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };
  
  return debouncedFn as ((...args: Parameters<T>) => void) & { cancel: () => void };
}

/**
 * Deep clone an object using native structuredClone with fallback
 */
export function deepClone<T>(obj: T): T {
  if (typeof structuredClone !== 'undefined') {
    return structuredClone(obj);
  }
  // Fallback for older browsers
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Pick specific properties from an object (lodash pick replacement)
 */
export function pick<T, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
  const result = {} as Pick<T, K>;
  for (const key of keys) {
    if (key in obj) {
      result[key] = obj[key];
    }
  }
  return result;
}

/**
 * Get unique values from array (lodash uniq replacement)
 */
export function uniq<T>(array: T[]): T[] {
  return Array.from(new Set(array));
}

/**
 * Check if value is empty (lodash isEmpty replacement)
 */
export function isEmpty(value: any): boolean {
  if (value == null) return true;
  if (Array.isArray(value) || typeof value === 'string') return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

/**
 * Clamp a number between min and max values
 */
export function clamp(number: number, min: number, max: number): number {
  return Math.min(Math.max(number, min), max);
}

export const CHANGE_REQUEST_STATUS_VALUES = [
  'DRAFT',
  'IN_REVIEW',
  'APPROVED',
  'MERGED',
  'CLOSED',
] as const;

export type NormalizedChangeRequestStatus =
  (typeof CHANGE_REQUEST_STATUS_VALUES)[number];

export function normalizeChangeRequestStatus(
  status?: string,
): NormalizedChangeRequestStatus | 'UNKNOWN' {
  if (!status) return 'UNKNOWN';
  return (CHANGE_REQUEST_STATUS_VALUES as readonly string[]).includes(status)
    ? (status as NormalizedChangeRequestStatus)
    : 'UNKNOWN';
}
